/**
 * Which contract rows the Customer profile shows for a seed (pure apart from the injected
 * lookups, unit-tested). Never invents a contract: a contract not in the contacts store is read
 * by its EXACT number from the contract records and shown with its real status; one found
 * nowhere gives `missing: 'none'` ("No contract ... found - check the number"), a failed read
 * `missing: 'error'`. A wrong number (one digit missing) used to open a made-up "Open"
 * contract (26 Sep 2026).
 */
import { contractKey, phoneDigits, isOpenStatus, identityKey } from '../services/contactsService';

// A contract shown alone: 'current' ("current rental only (no ID on file)") ONLY when it really has no ID - one with an
// ID is its identity's, even before the hourly contacts store holds it (the badge said "ID: 784-..." and "no ID on
// file" side by side - review, owner 28 Sep 2026). The store's own rule (contactsService.customerContracts) is the same.
const aloneBy = (identity) => (identityKey(identity).length >= 4 ? 'identity' : 'current');

/**
 * `store`: { customerContracts({ identity, contractNo, phone }) -> { by, rows } } - by: 'customer' (Ejar's customer number) | 'identity' | 'phone' |
 *   'current' (the contract alone, no ID on file) | 'none'.
 * `findRecord(no)`: -> { row } | null, throws when it could not check.
 * Returns { by, rows, missing } - rows: open ("Open" only) first, then the store's order.
 */
export async function resolveProfileRows(seed, { store, findRecord, onError } = {}) {
    let { by, rows } = store.customerContracts({ identity: seed.identity, contractNo: seed.contractNo });
    if ((!rows || rows.length === 0) && seed.contractNo) {
        let rec = null;
        let failed = false;
        try { rec = await findRecord(seed.contractNo); } catch (e) { failed = true; if (onError) onError(e); }
        if (rec && rec.row) {
            // the same customer rule as everywhere (contactsService.customerKeyOf): its identity, else - no ID on
            // file - the no-ID contracts of its phone, else the contract alone ('current' only with no ID - aloneBy)
            const same = store.customerContracts({ identity: rec.row.i, phone: rec.row.p, customer: rec.row.u });
            const others = (same.rows || []).filter((r) => r.c !== rec.row.c);
            rows = [rec.row, ...others];
            by = others.length ? same.by : aloneBy(rec.row.i);
        } else if (!failed && String(seed.status || '').trim()) {
            // a record the caller has just read itself (The Engine's live Ejar answer), not yet in
            // Firestore: shown with ITS status - never a default one
            rows = [{
                c: contractKey(seed.contractNo), n: seed.name || '', p: phoneDigits(seed.phone || ''),
                i: seed.identity || '', b: seed.branch || '', d: seed.pickupDate || '', e: seed.dropoffDate || '',
                s: String(seed.status).trim(), t: seed.customerType || '',
            }];
            by = aloneBy(seed.identity);
        } else {
            return { by: 'none', rows: [], missing: failed ? 'error' : 'none' };
        }
    }
    // The open contract always first; the rest keep their order (a stable sort).
    const sorted = [...(rows || [])].sort((a, b) => Number(isOpenStatus(b.s)) - Number(isOpenStatus(a.s)));
    return { by, rows: sorted, missing: null };
}

/** How many contracts are open now: Ejar's "Open" only (an empty status is not open). */
export const openNowCount = (rows) => (rows || []).filter((r) => isOpenStatus(r.s)).length;
